import React, { useEffect, useState, useCallback } from 'react';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Radar, Loader2, Phone, Mail, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

// Phase 1 — Continuous auto-prospecting control. Opt-in, default OFF.
// When enabled, the Brain sources up to `daily target` net-new ICP CONTACTS each
// day (people with a verified email — never company shells), optionally reveals a
// phone (call-ready), and auto-enrolls them into outreach (scoring + auto-cadence).
export const AutoProspectToggle = () => {
  const [enabled, setEnabled] = useState(false);
  const [target, setTarget] = useState(10);
  const [revealPhone, setRevealPhone] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      const r = await api.get('/api/brain/auto-prospect/report');
      setReport(r.data?.report || null);
    } catch (e) { /* non-blocking */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/automation-settings');
      const s = res.data?.settings || {};
      setEnabled(!!s.auto_prospect_enabled);
      setTarget(s.auto_prospect_daily_target || 10);
      setRevealPhone(s.auto_prospect_reveal_phone !== false);
    } catch (e) { /* non-blocking */ } finally { setLoading(false); }
    loadReport();
  }, [loadReport]);

  useEffect(() => { load(); }, [load]);

  const update = async (patch) => {
    setSaving(true);
    try {
      await api.put('/api/brain/automation-settings', patch);
      if ('auto_prospect_enabled' in patch) {
        setEnabled(patch.auto_prospect_enabled);
        toast.success(patch.auto_prospect_enabled ? 'Continuous auto-prospecting ON' : 'Auto-prospecting paused');
      }
      if ('auto_prospect_daily_target' in patch) toast.success(`Daily target set to ${patch.auto_prospect_daily_target}`);
      if ('auto_prospect_reveal_phone' in patch) {
        setRevealPhone(patch.auto_prospect_reveal_phone);
        toast.success(patch.auto_prospect_reveal_phone ? 'Phone enrichment ON — leads will be call-ready' : 'Phone enrichment OFF');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update auto-prospecting');
      load();
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div
      data-testid="auto-prospect-card"
      className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] px-4 py-3"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 p-2 rounded-lg bg-emerald-500/10">
            <Radar className="h-4 w-4 text-emerald-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Continuous auto-prospecting</p>
            <p className="text-xs text-muted-foreground">
              Brain sources net-new ICP <span className="font-medium">contacts</span> daily (verified email, never company shells) and auto-enrolls them into outreach. Spend stays within your daily cap.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {enabled && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">/day</span>
              <Input
                type="number" min="1" max="25"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                onBlur={() => {
                  const t = Math.max(1, Math.min(25, parseInt(target, 10) || 10));
                  setTarget(t);
                  update({ auto_prospect_daily_target: t });
                }}
                className="w-16 h-8 text-center"
                data-testid="auto-prospect-target"
              />
            </div>
          )}
          {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
          <Switch
            checked={enabled}
            onCheckedChange={(v) => update({ auto_prospect_enabled: v })}
            data-testid="auto-prospect-switch"
          />
        </div>
      </div>

      {enabled && (
        <div className="mt-3 pt-3 border-t border-emerald-500/15 flex flex-wrap items-center justify-between gap-3">
          {/* Phone enrichment toggle */}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-prospect-phone-row">
            <Phone className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-xs text-foreground">Pull verified phone (call-ready)</span>
            <Switch
              checked={revealPhone}
              onCheckedChange={(v) => update({ auto_prospect_reveal_phone: v })}
              data-testid="auto-prospect-phone-switch"
              className="scale-90"
            />
          </label>

          {/* Sourced-today ROI panel */}
          <div className="flex items-center gap-3 text-xs" data-testid="auto-prospect-report">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
              <span className="font-semibold text-foreground" data-testid="ap-sourced-today">
                {report?.sourced_today ?? 0}
              </span>
              sourced today
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Mail className="h-3 w-3" /> {report?.with_email ?? 0}
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Phone className="h-3 w-3" /> {report?.with_phone ?? 0}
            </span>
            <span className="text-muted-foreground">
              spend <span className="font-medium text-foreground">${(report?.credits_spent_usd ?? 0).toFixed(2)}</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoProspectToggle;
